import React, { useState } from 'react';
import { formatCurrency } from '../utils/currencyFormatter';
import { formatDate } from '../utils/dateFormatter';
import { TruckIcon, PlusIcon, SearchIcon, FilterIcon, ArrowDownIcon, ArrowUpIcon, EyeIcon, CalendarIcon, UserIcon, ClipboardListIcon, XIcon, FileTextIcon, PackageIcon, ShoppingCartIcon, CheckCircleIcon, AlertTriangleIcon, ClockIcon, LinkIcon } from 'lucide-react';
export function ReceiptList({
  onCreateReceipt,
  onViewReceiptDetails,
  locale = 'en-US'
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  // Sample data - in a real app this would come from your backend
  const sampleReceipts = [{
    id: 'GR-2023-001',
    date: '2023-11-05',
    receivedBy: 'John Smith',
    status: 'Complete',
    totalValue: 3000.0,
    currency: 'USD',
    purchaseOrders: [{
      poId: 'PO-2023-001',
      poName: 'Server Hardware',
      vendor: 'Server Solutions Inc.',
      items: [{
        id: 'ITEM-001',
        description: 'Dell XPS 15 Laptop',
        itemCode: 'LPT-001',
        quantity: 2,
        uom: 'EA',
        unitPrice: 1500.0,
        value: 3000.0,
        poQuantity: 5,
        remainingQuantity: 3,
        notes: 'Items received in good condition'
      }]
    }],
    deliveryDockets: [{
      id: 'DD-001-001',
      name: 'Delivery_Note_12345.pdf',
      uploadDate: '2023-11-05',
      uploadedBy: 'John Smith'
    }],
    notes: 'Partial delivery of laptops. Remaining units expected next week.',
    entity: 'Corporate',
    site: 'Headquarters',
    deliveryAddress: '123 Corporate Drive, Business Park, CA 94105',
    carrierInfo: 'FedEx',
    trackingNumber: '7891234567',
    inspectedBy: 'Alice Johnson',
    qualityCheck: 'Passed',
    inspectionDate: '2023-11-05'
  }, {
    id: 'GR-2023-002',
    date: '2023-11-10',
    receivedBy: 'Jane Doe',
    status: 'Partial',
    totalValue: 1250.0,
    currency: 'USD',
    purchaseOrders: [{
      poId: 'PO-2023-001',
      poName: 'Server Hardware',
      vendor: 'Server Solutions Inc.',
      items: [{
        id: 'ITEM-002',
        description: 'IT Setup and Configuration Services',
        itemCode: 'SVC-001',
        quantity: 0.5,
        uom: 'LOT',
        unitPrice: 2500.0,
        value: 1250.0,
        poQuantity: 1,
        remainingQuantity: 0.5,
        notes: 'Initial setup completed, final configuration pending'
      }]
    }],
    deliveryDockets: [],
    notes: 'Service partially completed. Final configuration scheduled for next month.',
    entity: 'Corporate',
    site: 'Headquarters',
    deliveryAddress: '123 Corporate Drive, Business Park, CA 94105',
    carrierInfo: 'N/A - Service',
    trackingNumber: 'N/A',
    inspectedBy: 'Robert Chen',
    qualityCheck: 'In Progress',
    inspectionDate: '2023-11-10'
  }, {
    id: 'GR-2023-003',
    date: '2023-10-10',
    receivedBy: 'Mark Johnson',
    status: 'Partial',
    totalValue: 750.0,
    currency: 'USD',
    purchaseOrders: [{
      poId: 'PO-2023-002',
      poName: 'Marketing Materials',
      vendor: 'PrintWorks Inc.',
      items: [{
        id: 'ITEM-003',
        description: 'Company Brochures',
        itemCode: 'BRO-001',
        quantity: 500,
        uom: 'EA',
        unitPrice: 1.5,
        value: 750.0,
        poQuantity: 1000,
        remainingQuantity: 500,
        notes: 'First batch of brochures received'
      }]
    }],
    deliveryDockets: [{
      id: 'DD-003-001',
      name: 'PrintWorks_Delivery_56789.pdf',
      uploadDate: '2023-10-10',
      uploadedBy: 'Mark Johnson'
    }],
    notes: 'First batch of marketing materials. Quality is excellent.',
    entity: 'Marketing',
    site: 'Headquarters',
    deliveryAddress: '123 Corporate Drive, Business Park, CA 94105',
    carrierInfo: 'UPS',
    trackingNumber: '1234567890',
    inspectedBy: 'Sarah Williams',
    qualityCheck: 'Passed',
    inspectionDate: '2023-10-10'
  }, {
    id: 'GR-2023-004',
    date: '2023-10-12',
    receivedBy: 'Mike Johnson',
    status: 'Complete',
    totalValue: 3950.5,
    currency: 'USD',
    purchaseOrders: [{
      poId: 'PO-2023-002',
      poName: 'Marketing Materials',
      vendor: 'PrintWorks Inc.',
      items: [{
        id: 'ITEM-004',
        description: 'Company Brochures',
        itemCode: 'BRO-001',
        quantity: 500,
        uom: 'EA',
        unitPrice: 1.5,
        value: 750.0,
        poQuantity: 1000,
        remainingQuantity: 0,
        notes: 'Final batch of brochures received'
      }, {
        id: 'ITEM-005',
        description: 'Banners',
        itemCode: 'BAN-001',
        quantity: 5,
        uom: 'EA',
        unitPrice: 340.1,
        value: 1700.5,
        poQuantity: 5,
        remainingQuantity: 0,
        notes: 'All banners received in good condition'
      }]
    }, {
      poId: 'PO-2023-005',
      poName: 'Trade Show Materials',
      vendor: 'PrintWorks Inc.',
      items: [{
        id: 'ITEM-006',
        description: 'Display Stands',
        itemCode: 'DSP-001',
        quantity: 3,
        uom: 'EA',
        unitPrice: 500.0,
        value: 1500.0,
        poQuantity: 3,
        remainingQuantity: 0,
        notes: 'Display stands arrived in good condition'
      }]
    }],
    deliveryDockets: [{
      id: 'DD-004-001',
      name: 'PrintWorks_Delivery_56790.pdf',
      uploadDate: '2023-10-12',
      uploadedBy: 'Mike Johnson'
    }, {
      id: 'DD-004-002',
      name: 'PrintWorks_TradeShow_Delivery_78901.pdf',
      uploadDate: '2023-10-12',
      uploadedBy: 'Mike Johnson'
    }],
    notes: 'Combined delivery of marketing materials and trade show equipment from PrintWorks. All items received in good condition.',
    entity: 'Marketing',
    site: 'Headquarters',
    deliveryAddress: '123 Corporate Drive, Business Park, CA 94105',
    carrierInfo: 'UPS',
    trackingNumber: '0987654321',
    inspectedBy: 'Sarah Williams',
    qualityCheck: 'Passed',
    inspectionDate: '2023-10-12'
  }, {
    id: 'GR-2023-005',
    date: '2023-10-15',
    receivedBy: 'Robert Chen',
    status: 'Complete',
    totalValue: 4750.0,
    currency: 'USD',
    purchaseOrders: [{
      poId: 'PO-2023-007',
      poName: 'Office Computers',
      vendor: 'Tech Solutions Inc.',
      items: [{
        id: 'ITEM-007',
        description: 'Desktop Computer',
        itemCode: 'DTC-001',
        quantity: 2,
        uom: 'EA',
        unitPrice: 1200.0,
        value: 2400.0,
        poQuantity: 2,
        remainingQuantity: 0,
        notes: 'Computers arrived in perfect condition'
      }]
    }, {
      poId: 'PO-2023-008',
      poName: 'Office Peripherals',
      vendor: 'Tech Solutions Inc.',
      items: [{
        id: 'ITEM-008',
        description: 'Monitor 27"',
        itemCode: 'MON-001',
        quantity: 3,
        uom: 'EA',
        unitPrice: 350.0,
        value: 1050.0,
        poQuantity: 3,
        remainingQuantity: 0,
        notes: 'All monitors in good condition'
      }, {
        id: 'ITEM-009',
        description: 'Keyboard and Mouse Set',
        itemCode: 'KMS-001',
        quantity: 5,
        uom: 'SET',
        unitPrice: 60.0,
        value: 300.0,
        poQuantity: 5,
        remainingQuantity: 0,
        notes: 'All peripherals working correctly'
      }]
    }, {
      poId: 'PO-2023-009',
      poName: 'Office Network Equipment',
      vendor: 'Tech Solutions Inc.',
      items: [{
        id: 'ITEM-010',
        description: 'Network Switch',
        itemCode: 'NSW-001',
        quantity: 1,
        uom: 'EA',
        unitPrice: 1000.0,
        value: 1000.0,
        poQuantity: 1,
        remainingQuantity: 0,
        notes: 'Network switch installed and tested'
      }]
    }],
    deliveryDockets: [{
      id: 'DD-005-001',
      name: 'TechSolutions_Delivery_12345.pdf',
      uploadDate: '2023-10-15',
      uploadedBy: 'Robert Chen'
    }],
    notes: 'Complete delivery of all IT equipment from Tech Solutions Inc. All items tested and working correctly.',
    entity: 'IT Department',
    site: 'Headquarters',
    deliveryAddress: '123 Corporate Drive, Business Park, CA 94105',
    carrierInfo: 'DHL',
    trackingNumber: '123456789012',
    inspectedBy: 'Robert Chen',
    qualityCheck: 'Passed',
    inspectionDate: '2023-10-15'
  }];
  // Get primary vendor for display in the table
  const getPrimaryVendor = receipt => {
    if (!receipt.purchaseOrders || receipt.purchaseOrders.length === 0) {
      return 'N/A';
    }
    return receipt.purchaseOrders[0].vendor;
  };
  // Get primary PO for display in the table
  const getPrimaryPO = receipt => {
    if (!receipt.purchaseOrders || receipt.purchaseOrders.length === 0) {
      return 'N/A';
    }
    return receipt.purchaseOrders[0].poId;
  };
  // Get all items from all POs
  const getAllItems = receipt => {
    if (!receipt.purchaseOrders) return [];
    let allItems = [];
    receipt.purchaseOrders.forEach(po => {
      if (po.items) {
        allItems = [...allItems, ...po.items.map(item => ({
          ...item,
          poId: po.poId,
          poName: po.poName,
          vendor: po.vendor
        }))];
      }
    });
    return allItems;
  };
  // Filter receipts based on search query and status filter
  const filteredReceipts = sampleReceipts.filter(receipt => {
    const matchesSearch = receipt.id.toLowerCase().includes(searchQuery.toLowerCase()) || receipt.purchaseOrders && receipt.purchaseOrders.some(po => po.poId.toLowerCase().includes(searchQuery.toLowerCase()) || po.poName.toLowerCase().includes(searchQuery.toLowerCase()) || po.vendor.toLowerCase().includes(searchQuery.toLowerCase()));
    if (filterStatus === 'all') {
      return matchesSearch;
    }
    return matchesSearch && receipt.status.toLowerCase() === filterStatus.toLowerCase();
  });
  // Sort receipts
  const sortedReceipts = [...filteredReceipts].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    // Handle special case for date
    if (sortField === 'date') {
      aValue = new Date(a.date).getTime();
      bValue = new Date(b.date).getTime();
    }
    // Handle sorting by vendor or PO
    else if (sortField === 'vendor') {
      aValue = getPrimaryVendor(a);
      bValue = getPrimaryVendor(b);
    } else if (sortField === 'poId') {
      aValue = getPrimaryPO(a);
      bValue = getPrimaryPO(b);
    }
    if (aValue < bValue) {
      return sortDirection === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortDirection === 'asc' ? 1 : -1;
    }
    return 0;
  });
  // Handle sort click
  const handleSortClick = field => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Render sort indicator
  const renderSortIndicator = field => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4 ml-1" data-id="element-14879" /> : <ArrowDownIcon className="h-4 w-4 ml-1" data-id="element-14880" />;
  };
  // Handle view receipt details
  const handleViewReceipt = receipt => {
    setSelectedReceipt(receipt);
    setShowDetailsModal(true);
    // Comment out the parent handler call to prevent the alert
    // This was causing the error when clicking View
    // if (onViewReceiptDetails) {
    //   onViewReceiptDetails(receipt)
    // }
  };
  // Close the details modal
  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedReceipt(null);
  };
  return <div className="space-y-6" data-id="element-14881">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-14882">
        <div className="w-full sm:w-96" data-id="element-14883">
          <div className="relative rounded-md shadow-sm" data-id="element-14884">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-14885">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-14886" />
            </div>
            <input type="text" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search by GR#, PO#, or vendor" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-14887" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto" data-id="element-14888">
          <div className="relative inline-block text-left" data-id="element-14889">
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-14890">
              <option value="all" data-id="element-14891">All Statuses</option>
              <option value="complete" data-id="element-14892">Complete</option>
              <option value="partial" data-id="element-14893">Partial</option>
            </select>
          </div>
          <button onClick={() => onCreateReceipt()} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-14894">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-14895" />
            Create New Receipt
          </button>
        </div>
      </div>
      <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-14896">
        <div className="overflow-x-auto" data-id="element-14897">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-14898">
            <thead className="bg-gray-50" data-id="element-14899">
              <tr data-id="element-14900">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSortClick('id')} data-id="element-14901">
                  <div className="flex items-center" data-id="element-14902">
                    Receipt # {renderSortIndicator('id')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSortClick('poId')} data-id="element-14903">
                  <div className="flex items-center" data-id="element-14904">
                    PO # {renderSortIndicator('poId')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSortClick('vendor')} data-id="element-14905">
                  <div className="flex items-center" data-id="element-14906">
                    Vendor {renderSortIndicator('vendor')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSortClick('date')} data-id="element-14907">
                  <div className="flex items-center" data-id="element-14908">
                    Date {renderSortIndicator('date')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSortClick('receivedBy')} data-id="element-14909">
                  <div className="flex items-center" data-id="element-14910">
                    Received By {renderSortIndicator('receivedBy')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSortClick('status')} data-id="element-14911">
                  <div className="flex items-center" data-id="element-14912">
                    Status {renderSortIndicator('status')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSortClick('totalValue')} data-id="element-14913">
                  <div className="flex items-center" data-id="element-14914">
                    Value {renderSortIndicator('totalValue')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-14915">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-14916">
              {sortedReceipts.length > 0 ? sortedReceipts.map(receipt => <tr key={receipt.id} className="hover:bg-gray-50" data-id="element-14917">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600" data-id="element-14918">
                      {receipt.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14919">
                      <div className="flex flex-col" data-id="element-14920">
                        <div className="flex items-center" data-id="element-14921">
                          {getPrimaryPO(receipt)}
                        </div>
                        {receipt.purchaseOrders && receipt.purchaseOrders.length > 1 && <div className="text-xs text-gray-400 mt-1 flex items-center" data-id="element-14922">
                              <LinkIcon className="h-3 w-3 mr-1" data-id="element-14923" />
                              {receipt.purchaseOrders.length} POs
                            </div>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14924">
                      <div className="flex flex-col" data-id="element-14925">
                        <div className="flex items-center" data-id="element-14926">
                          {getPrimaryVendor(receipt)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14927">
                      <div className="flex items-center" data-id="element-14928">
                        <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-14929" />
                        {formatDate(receipt.date, locale)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14930">
                      <div className="flex items-center" data-id="element-14931">
                        <UserIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-14932" />
                        {receipt.receivedBy}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-14933">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${receipt.status === 'Complete' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-14934">
                        {receipt.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-14935">
                      {formatCurrency(receipt.totalValue, receipt.currency, locale)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-14936">
                      <button onClick={() => handleViewReceipt(receipt)} className="text-indigo-600 hover:text-indigo-900 inline-flex items-center" data-id="element-14937">
                        <EyeIcon className="h-4 w-4 mr-1" data-id="element-14938" />
                        View
                      </button>
                    </td>
                  </tr>) : <tr data-id="element-14939">
                  <td colSpan={8} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-14940">
                    No receipts found
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex items-center justify-between mt-4 text-sm text-gray-600" data-id="element-14941">
        <div data-id="element-14942">
          Showing {sortedReceipts.length} of {sampleReceipts.length} receipts
        </div>
      </div>
      {/* Receipt Details Modal */}
      {showDetailsModal && selectedReceipt && <div className="fixed z-10 inset-0 overflow-y-auto" data-id="element-14943">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-14944">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-14945">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-14946"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-14947">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full" data-id="element-14948">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-14949">
                <div className="flex justify-between items-start pb-3 border-b border-gray-200" data-id="element-14950">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center" data-id="element-14951">
                    <TruckIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-14952" />
                    Goods Receipt Details - {selectedReceipt.id}
                  </h3>
                  <button onClick={closeDetailsModal} className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none" data-id="element-14953">
                    <XIcon className="h-6 w-6" data-id="element-14954" />
                  </button>
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-14955">
                  {/* Left Column - Receipt Details */}
                  <div data-id="element-14956">
                    <div className="bg-gray-50 p-4 rounded-md mb-4" data-id="element-14957">
                      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center" data-id="element-14958">
                        <FileTextIcon className="h-4 w-4 mr-2 text-indigo-500" data-id="element-14959" />
                        Receipt Information
                      </h4>
                      <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-14960">
                        <div data-id="element-14961">
                          <p className="text-gray-500" data-id="element-14962">Receipt Date:</p>
                          <p className="font-medium" data-id="element-14963">
                            {formatDate(selectedReceipt.date, locale)}
                          </p>
                        </div>
                        <div data-id="element-14964">
                          <p className="text-gray-500" data-id="element-14965">Received By:</p>
                          <p className="font-medium" data-id="element-14966">
                            {selectedReceipt.receivedBy}
                          </p>
                        </div>
                        <div data-id="element-14967">
                          <p className="text-gray-500" data-id="element-14968">Status:</p>
                          <p className="font-medium" data-id="element-14969">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${selectedReceipt.status === 'Complete' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-14970">
                              {selectedReceipt.status}
                            </span>
                          </p>
                        </div>
                        <div data-id="element-14971">
                          <p className="text-gray-500" data-id="element-14972">Entity/Site:</p>
                          <p className="font-medium" data-id="element-14973">
                            {selectedReceipt.entity} / {selectedReceipt.site}
                          </p>
                        </div>
                        <div data-id="element-14974">
                          <p className="text-gray-500" data-id="element-14975">Inspected By:</p>
                          <p className="font-medium" data-id="element-14976">
                            {selectedReceipt.inspectedBy}
                          </p>
                        </div>
                        <div data-id="element-14977">
                          <p className="text-gray-500" data-id="element-14978">Inspection Date:</p>
                          <p className="font-medium" data-id="element-14979">
                            {formatDate(selectedReceipt.inspectionDate, locale)}
                          </p>
                        </div>
                        <div data-id="element-14980">
                          <p className="text-gray-500" data-id="element-14981">Quality Check:</p>
                          <p className="font-medium" data-id="element-14982">
                            {selectedReceipt.qualityCheck}
                          </p>
                        </div>
                        <div data-id="element-14983">
                          <p className="text-gray-500" data-id="element-14984">PO Count:</p>
                          <p className="font-medium" data-id="element-14985">
                            {selectedReceipt.purchaseOrders ? selectedReceipt.purchaseOrders.length : 0}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-md" data-id="element-14986">
                      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center" data-id="element-14987">
                        <ShoppingCartIcon className="h-4 w-4 mr-2 text-indigo-500" data-id="element-14988" />
                        Purchase Order Information
                      </h4>
                      {selectedReceipt.purchaseOrders && selectedReceipt.purchaseOrders.map((po, index) => <div key={po.poId} className={`${index > 0 ? 'mt-3 pt-3 border-t border-gray-200' : ''}`} data-id="element-14989">
                            <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-14990">
                              <div data-id="element-14991">
                                <p className="text-gray-500" data-id="element-14992">PO Number:</p>
                                <p className="font-medium text-indigo-600" data-id="element-14993">
                                  {po.poId}
                                </p>
                              </div>
                              <div data-id="element-14994">
                                <p className="text-gray-500" data-id="element-14995">PO Name:</p>
                                <p className="font-medium" data-id="element-14996">{po.poName}</p>
                              </div>
                              <div data-id="element-14997">
                                <p className="text-gray-500" data-id="element-14998">Vendor:</p>
                                <p className="font-medium" data-id="element-14999">{po.vendor}</p>
                              </div>
                              <div data-id="element-15000">
                                <p className="text-gray-500" data-id="element-15001">Currency:</p>
                                <p className="font-medium" data-id="element-15002">
                                  {selectedReceipt.currency}
                                </p>
                              </div>
                            </div>
                          </div>)}
                      <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-15003">
                        <div className="text-sm" data-id="element-15004">
                          <p className="text-gray-500" data-id="element-15005">Delivery Address:</p>
                          <p className="font-medium" data-id="element-15006">
                            {selectedReceipt.deliveryAddress}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Right Column - Shipping and Notes */}
                  <div data-id="element-15007">
                    <div className="bg-gray-50 p-4 rounded-md mb-4" data-id="element-15008">
                      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center" data-id="element-15009">
                        <TruckIcon className="h-4 w-4 mr-2 text-indigo-500" data-id="element-15010" />
                        Shipping Information
                      </h4>
                      <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-15011">
                        <div data-id="element-15012">
                          <p className="text-gray-500" data-id="element-15013">Carrier:</p>
                          <p className="font-medium" data-id="element-15014">
                            {selectedReceipt.carrierInfo}
                          </p>
                        </div>
                        <div data-id="element-15015">
                          <p className="text-gray-500" data-id="element-15016">Tracking Number:</p>
                          <p className="font-medium" data-id="element-15017">
                            {selectedReceipt.trackingNumber}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-md mb-4" data-id="element-15018">
                      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center" data-id="element-15019">
                        <FileTextIcon className="h-4 w-4 mr-2 text-indigo-500" data-id="element-15020" />
                        Notes
                      </h4>
                      <p className="text-sm" data-id="element-15021">{selectedReceipt.notes}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-md" data-id="element-15022">
                      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center" data-id="element-15023">
                        <FileTextIcon className="h-4 w-4 mr-2 text-indigo-500" data-id="element-15024" />
                        Delivery Dockets
                      </h4>
                      {selectedReceipt.deliveryDockets && selectedReceipt.deliveryDockets.length > 0 ? <ul className="divide-y divide-gray-200" data-id="element-15025">
                          {selectedReceipt.deliveryDockets.map(docket => <li key={docket.id} className="py-2 flex justify-between" data-id="element-15026">
                              <div className="flex items-center" data-id="element-15027">
                                <FileTextIcon className="h-4 w-4 mr-2 text-gray-400" data-id="element-15028" />
                                <span className="text-sm" data-id="element-15029">{docket.name}</span>
                              </div>
                              <div className="flex items-center" data-id="element-15030">
                                <span className="text-xs text-gray-500 mr-2" data-id="element-15031">
                                  {formatDate(docket.uploadDate, locale)}
                                </span>
                                <button className="text-indigo-600 text-sm hover:text-indigo-800" data-id="element-15032">
                                  View
                                </button>
                              </div>
                            </li>)}
                        </ul> : <p className="text-sm text-gray-500 italic" data-id="element-15033">
                          No delivery dockets attached
                        </p>}
                    </div>
                  </div>
                </div>
                {/* Receipt Line Items */}
                <div className="mt-6" data-id="element-15034">
                  <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center" data-id="element-15035">
                    <PackageIcon className="h-4 w-4 mr-2 text-indigo-500" data-id="element-15036" />
                    Receipt Line Items
                  </h4>
                  <div className="overflow-x-auto border border-gray-200 rounded-md" data-id="element-15037">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-15038">
                      <thead className="bg-gray-50" data-id="element-15039">
                        <tr data-id="element-15040">
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15041">
                            PO #
                          </th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15042">
                            Item Code
                          </th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15043">
                            Description
                          </th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15044">
                            Ordered
                          </th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15045">
                            Received
                          </th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15046">
                            Remaining
                          </th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15047">
                            Unit Price
                          </th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15048">
                            Value
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-15049">
                        {getAllItems(selectedReceipt).map(item => <tr key={`${item.poId}-${item.id}`} className="hover:bg-gray-50" data-id="element-15050">
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-indigo-600" data-id="element-15051">
                              {item.poId}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-15052">
                              {item.itemCode}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-15053">
                              {item.description}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-15054">
                              {item.poQuantity} {item.uom}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-15055">
                              {item.quantity} {item.uom}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-15056">
                              {item.remainingQuantity} {item.uom}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-15057">
                              {formatCurrency(item.unitPrice, selectedReceipt.currency, locale)}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-15058">
                              {formatCurrency(item.value, selectedReceipt.currency, locale)}
                            </td>
                          </tr>)}
                        <tr className="bg-gray-50" data-id="element-15059">
                          <td colSpan="7" className="px-3 py-2 text-right text-sm font-medium text-gray-900" data-id="element-15060">
                            Total Value:
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-15061">
                            {formatCurrency(selectedReceipt.totalValue, selectedReceipt.currency, locale)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                {/* Receipt Status Indicators */}
                <div className="mt-6 flex flex-wrap gap-4" data-id="element-15062">
                  <div className="flex items-center text-sm" data-id="element-15063">
                    <div className={`flex-shrink-0 h-4 w-4 rounded-full ${selectedReceipt.status === 'Complete' ? 'bg-green-500' : 'bg-yellow-500'} mr-1.5`} data-id="element-15064"></div>
                    <p className="text-gray-700" data-id="element-15065">
                      Receipt Status:{' '}
                      <span className="font-medium" data-id="element-15066">
                        {selectedReceipt.status}
                      </span>
                    </p>
                  </div>
                  {selectedReceipt.status === 'Complete' ? <div className="flex items-center text-sm text-green-700" data-id="element-15067">
                      <CheckCircleIcon className="h-4 w-4 mr-1.5" data-id="element-15068" />
                      <p data-id="element-15069">All items received</p>
                    </div> : <div className="flex items-center text-sm text-yellow-700" data-id="element-15070">
                      <ClockIcon className="h-4 w-4 mr-1.5" data-id="element-15071" />
                      <p data-id="element-15072">Partially received - awaiting further deliveries</p>
                    </div>}
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-15073">
                <button type="button" onClick={closeDetailsModal} className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-15074">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}